import React, { useCallback, useEffect, useState } from 'react'
import TabGroup, { Tab } from 'components/common/TabGroup/TabGroup'
import {
    acknowledgeForumEntry,
    closeShowChannel,
    getShowChannel,
    getTopAcknowledgedEntries,
    getTopUnacknowledgedEntries,
    retrieveForum,
} from 'redux/slices/interaction'
import { editNote, getEditingNote, getNotes } from 'redux/slices/showNotes'
import { getShow } from 'redux/slices/liveShow'
import { retrieveArtistShow } from 'redux/slices/liveShow/thunks'
import { fetchViewerBreakdown, getViewerBreakdown, getViewerCounter, fetchViewerCounter } from 'redux/slices/viewer'
import { luaBatchReceived, setLuasActive } from 'redux/slices/liveUserActions'
import { useDispatch, useSelector } from 'react-redux'

import { ArtistNotes } from './artist-notes/ArtistNotes'
import { AskTheArtistViewer } from './ask-the-artist-viewer/AskTheArtistViewer'
import Constants from 'constants.js'
import LuaContainer from 'components/set/LuaContainer/LuaContainer'
import { NoteEditor } from 'components/note-editor/NoteEditor'
import Poll from './poll/Poll'
import ShowDuration from './ShowDuration/ShowDuration'
import ViewerByCountryList from './ViewerByCountryList/ViewerByCountryList'
import clsx from 'clsx'
import logoWhite from 'assets/logo-iso-white.svg'
import { useRouteMatch } from 'react-router-dom'
import { useStyles } from './styles'
import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary'
import { Timer } from 'redux/slices/timers'
import { clearTimeoutFromStore } from 'redux/slices/timers/thunks'

const ArtistDashboard = () => {
    const match = useRouteMatch()
    const classes = useStyles()
    const dispatch = useDispatch()
    const [tab, setTab] = useState(0)

    const show = useSelector(getShow)
    const wsChannel = useSelector(getShowChannel)
    const notes = useSelector(getNotes)
    const viewerCount = useSelector(getViewerCounter)
    const forumAcknowledged = useSelector(getTopAcknowledgedEntries)
    const forumUnacknowledged = useSelector(getTopUnacknowledgedEntries)
    const viewerBreakdown = useSelector(getViewerBreakdown)
    const editedNote = useSelector(getEditingNote)

    const handleNoteEdit = useCallback(note => dispatch(editNote(note)), [dispatch])

    const handleEntryAcknowledgement = useCallback(entry => dispatch(acknowledgeForumEntry(entry)), [dispatch])

    useEffect(() => {
        if (!show) {
            dispatch(retrieveArtistShow(match.params.showId))
            dispatch(setLuasActive(true))
        }
    }, [dispatch, show, match.params.showId])

    useEffect(() => {
        if (show) {
            const fetchForumEntries = () => {
                dispatch(
                    retrieveForum(show._id, { count: Constants.ARTIST_DASHBOARD_MAX_QUESTIONS, acknowledged: true }),
                )
            }
            const fetchViewersByCountry = () => {
                dispatch(fetchViewerBreakdown(show._id))
            }
            const fetchViewerCount = () => {
                dispatch(fetchViewerCounter(show._id))
            }

            const intervals = [
                setInterval(fetchForumEntries, Constants.ARTIST_DASHBOARD_FORUM_POLLING_INTERVAL),
                setInterval(fetchViewersByCountry, Constants.ARTIST_DASHBOARD_VIEWERS_POLLING_INTERVAL),
                setInterval(fetchViewerCount, Constants.ARTIST_DASHBOARD_VIEWERS_POLLING_INTERVAL),
            ]

            fetchForumEntries()
            fetchViewersByCountry()
            fetchViewerCount()

            return () => {
                dispatch(closeShowChannel(show.channelName))
                intervals.forEach(clearInterval)
                dispatch(clearTimeoutFromStore(Timer.WORDFLOW_TIME_LEFT))
            }
        }
    }, [dispatch, show])

    useEffect(() => {
        const handleLuaBatch = async ({ luas }) => dispatch(luaBatchReceived(luas))

        wsChannel && wsChannel.bind(Constants.WEBSOCKET_CHAT_LUA_BATCH, handleLuaBatch)

        return () => {
            wsChannel && wsChannel.unbind(Constants.WEBSOCKET_CHAT_LUA_BATCH, handleLuaBatch)
        }
    }, [wsChannel, dispatch])

    return (
        show &&
        show.artist && (
            <ErrorBoundary name="Artist Dashboard" showModal>
                <div className={classes.dashboard}>
                    {editedNote && <NoteEditor showId={show._id} />}
                    <aside className={classes.sidebar}>
                        <img className={classes.logo} src={logoWhite} alt="Logo"></img>
                    </aside>
                    <div className={classes.left}>
                        <div className={classes.widget}>
                            <h3 className={clsx(classes.widgetTitle, classes.textLeft)}>{show.title}</h3>
                            <h1 className={classes.title}>{show.title}</h1>
                        </div>
                        <TabGroup value={tab} onChange={setTab}>
                            <Tab label="Q&A" />
                            <Tab label="Poll" />
                            <Tab label="Notes" />
                        </TabGroup>
                        {tab === 0 && (
                            <AskTheArtistViewer
                                acknowledgedEntries={forumAcknowledged}
                                unacknowledgedEntries={forumUnacknowledged}
                                onAcknowledge={handleEntryAcknowledgement}
                            />
                        )}
                        {tab === 1 && <Poll />}
                        {tab === 2 && <ArtistNotes onNoteEdit={handleNoteEdit} notes={notes} />}
                    </div>
                    <div className={classes.right}>
                        <div className={classes.widget}>
                            <h3 className={classes.widgetTitle}>Show Duration</h3>
                            <ShowDuration show={show}></ShowDuration>
                        </div>
                        <div className={classes.widget} style={{ minHeight: 116 }}>
                            <h3 className={classes.widgetTitle}>Engagement</h3>
                            <LuaContainer horizontal travelDistance="320px" />
                        </div>
                        <div className={classes.widgetContainer}>
                            <div className={classes.widget}>
                                <h3 className={classes.widgetTitle}>Total Views</h3>
                                <p className={classes.count}>--</p>
                            </div>
                            <div className={classes.widget}>
                                <h3 className={classes.widgetTitle}>Live Viewers</h3>
                                <p className={classes.count}>{viewerCount}</p>
                            </div>
                        </div>
                        <div className={classes.widget}>
                            <h3 className={classes.widgetTitle}>Viewers By Country</h3>
                            {viewerBreakdown && <ViewerByCountryList data={viewerBreakdown} />}
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        )
    )
}

export default React.memo(ArtistDashboard)
